import { convertArrowFromServerModel } from './ArrowServerModel';
import { convertToBlindMapQuestion } from './BlindMapQuestionServerModel';
import { ContentConverter } from './ContentConverter';
import { ContentTypeServerEnum } from './ContentItem';
import { convertToHotspotTab } from './HotspotTabsServerModel';
import { ItemTypeServer, } from './Item';
import { convertModalSizeFromServerModel } from './PropertiesServerModel';
import { convertToQuestion } from './QuestionServerModel';
import { convertToStartingMessage } from './StartingMessageServerModel';
import { convertToStep } from './StepServerModel';
import * as Immutable from 'immutable';
import { List } from 'immutable';
import { filterOutNullsy } from '../utils/filterOutNullsy';
import { createParentId, createParentIdForResourceFolder } from '../content/parentId';
import { isResourcesFolder } from '../content/itemTypeguards';
import { MenuItemHelper } from '../content/MenuItem';
import { NonSequencedFlowItemHelper } from '../content/NonSequencedFlowItem';
import { SequencedFlowItemHelper } from '../content/SequencedFlowItem';
import { SectionHelper } from '../content/contentBuilder/Section';
import { HotspotHelper } from '../content/Hotspot';
import { ResourceHelper } from '../content/Resource';
import { ResourcesFolderHelper } from '../content/ResourcesFolder';
import { QuizHelper } from '../content/Quiz';
export const convertItemsFunctionCreator = (isInvalidItemInStudentMode) => {
    /*
  Converts any item from server into its domain representation. For menu item it calls itself recursively for all children.
   */
    const convertItem = (serverModel, parentId) => {
        switch (serverModel.itemType) {
            case ItemTypeServer.menuItem:
                return convertMenuItem(serverModel, parentId);
            case ItemTypeServer.nonSequencedFlow:
                return convertNonSequenced(serverModel, parentId);
            case ItemTypeServer.sequencedFlow:
                return convertSequencedFlow(serverModel, parentId);
            case ItemTypeServer.quiz:
                return convertQuiz(serverModel, parentId);
            case ItemTypeServer.resources:
                return convertResourcesFolder(serverModel, createParentIdForResourceFolder());
        }
    };
    const addItemToRootItemsIfNotResource = (rootItems, item) => {
        return isResourcesFolder(item) ? rootItems : rootItems.push(item.id);
    };
    /*
  Called on the end of conversion to extract root item ID and to take all items and map them based on their IDs for easier accessibility
   */
    const normalizeConvertedItems = (reduction, itemServer, parentId) => {
        const conversionResult = convertItem(itemServer, parentId);
        if (!conversionResult) {
            return reduction;
        }
        const allNewItems = [conversionResult.parent, ...conversionResult.allSubitems];
        const newItemsMap = Immutable.OrderedMap(allNewItems.map((item) => [item.id, item]));
        const updatedItemsMap = reduction.itemsMap.concat(newItemsMap).toOrderedMap();
        const rootItem = conversionResult.parent;
        const updatedRootLevelItemIds = addItemToRootItemsIfNotResource(reduction.rootLevelItemIds, rootItem);
        return {
            itemsMap: updatedItemsMap,
            rootLevelItemIds: updatedRootLevelItemIds,
        };
    };
    /*
  Entry point of conversion, converts multiple server items into map of their domain representations.
   */
    const convertItems = (serverItems, parentItemId) => {
        const conversion = serverItems.reduce((reduction, itemServer) => normalizeConvertedItems(reduction, itemServer, parentItemId), { itemsMap: Immutable.OrderedMap(), rootLevelItemIds: List() });
        return conversion;
    };
    /*
  Helper method for leaf items which never have any subitems.
   */
    const createConversionResultForContentItem = (item) => {
        if (!item) {
            return null;
        }
        return {
            parent: item,
            allSubitems: [],
        };
    };
    /*
  Helper method for excluding invalid domain models.
   */
    const getValidDomainModelOnly = (domainModel) => {
        if (!domainModel || isInvalidItemInStudentMode(domainModel)) {
            return null;
        }
        return domainModel;
    };
    /*
  Helper method to take all children and creates array from them. Also takes all children and all their subitems and
  merges them into one array. Ignores failed conversions.
   */
    const mergeConversionResults = (prev, current) => {
        return {
            immediateChildren: prev.immediateChildren.concat(current.parent),
            allSubitems: prev.allSubitems.concat(current.parent).concat(current.allSubitems),
        };
    };
    /*
  Converts all children and then creates structure which contains all immediate children in one array and all valid
  subitems (also immediate children) in other array.
   */
    const aggregateConvertedChildren = (childrenServerModels, convertChild, parentId) => {
        return filterOutNullsy(childrenServerModels.map((childServerModel) => {
            return convertChild(childServerModel, parentId);
        })).reduce((acc, result) => {
            return mergeConversionResults(acc, result);
        }, { immediateChildren: [], allSubitems: [] });
    };
    /*
  Core method of conversion. Takes server model parent with server model children and converts them.
  Returns conversion result which is either null if it was impossible to create valid domain model, or structure which contains
  converted parent and all converted subitems (including immediate children).
  Can be called recursively for items which have content items which have also content items (Seq. flow, nonSequenced flow)
   */
    const convertItemWithChildren = (getChildren, convertChild, convertParent, serverModel, parentId) => {
        const childrenServerModels = getChildren(serverModel);
        const aggregatedChildren = aggregateConvertedChildren(childrenServerModels, convertChild, createParentId(serverModel.guid));
        const domainModel = convertParent(serverModel, getItemIds(aggregatedChildren.immediateChildren), parentId);
        if (!domainModel) {
            return null;
        }
        return {
            parent: domainModel,
            allSubitems: aggregatedChildren.allSubitems,
        };
    };
    /*
  Configuration for conversion, adjustable for all items and content items which have children.
  Takes method which provides children of given server item, then additional method to convert each of those children
  into valid domain model and third method to create valid domain model from inputted parent.
  Returns method which takes server model and returns it converted.
   */
    const createConvertItemWithChildren = ({ getChildren, convertChild, convertParent, }) => (serverModel, parentId) => {
        return convertItemWithChildren(getChildren, convertChild, convertParent, serverModel, parentId);
    };
    /*
  Helper method to extract id of item.
   */
    const getItemIds = (children) => List(children.map((item) => item.id));
    /*
  Converts and adds starting message to converted nonsequenced flow or menu item , does nothing if server variant doesn't have starting message
   */
    const addConvertedStartingMessage = (conversionResult, serverModel) => {
        if (!serverModel.startingMessage || !conversionResult) {
            return conversionResult;
        }
        const startingMessage = convertToStartingMessage(serverModel.startingMessage, conversionResult.parent.id);
        conversionResult.parent.startingMessageId = startingMessage.id;
        const subitemsWithStartingMessage = [...conversionResult.allSubitems, startingMessage];
        return {
            parent: conversionResult.parent,
            allSubitems: subitemsWithStartingMessage,
        };
    };
    // Sequenced flow
    /*
  Method which helps us process Split server models same as all other content items. As Split opposed to all other
  server content items returns multiple content items when converted instead of 1-1 conversion, default conversion couldn't process it.
  Instead of adjusting conversion, we just extract sections from split when getting children of Sequenced flow/Section so
  we can just then convert each section on its own and we can use default converter.
   */
    const getChildrenWithSectionsExtractedFromSplit = (serverModel) => {
        const children = serverModel.contentItems;
        const spreadChildren = children.reduce((acc, child) => {
            if (child.itemType !== ContentTypeServerEnum.Split) {
                return [...acc, child];
            }
            const split = child;
            const sections = split.sections;
            return [...acc, ...sections];
        }, []);
        return spreadChildren;
    };
    /*
  Creates converter, configurable for either Section or Sequenced flow conversion, based on outputted domain model.
   */
    const createSequencedFlowSegmentConverter = (createDomainModel) => {
        return createConvertItemWithChildren({
            getChildren: getChildrenWithSectionsExtractedFromSplit,
            convertChild: (serverModel, parentId) => convertSequencedFlowContentItems(serverModel, parentId),
            convertParent: createDomainModel,
        });
    };
    const createSequencedFlowModel = (serverModel, childrenIds, parentId) => {
        return SequencedFlowItemHelper.build({
            contentItems: childrenIds,
            title: serverModel.name,
            trackingIdentifier: serverModel.trackingIdentifier,
            isMandatory: serverModel.isMandatory,
            id: serverModel.guid,
            parentId,
        });
    };
    const convertSequencedFlow = (serverModel, parentId) => {
        return createSequencedFlowSegmentConverter(createSequencedFlowModel)(serverModel, parentId);
    };
    const convertSection = createSequencedFlowSegmentConverter((serverModel, childrenIds, parentId) => {
        return getValidDomainModelOnly(SectionHelper.build({
            children: childrenIds,
            title: serverModel.title,
            trackingIdentifier: serverModel.trackingIdentifier,
            id: serverModel.guid,
            parentId,
        }));
    });
    const convertSequencedFlowContentItems = (contentItem, parentId) => {
        switch (contentItem.itemType) {
            case ContentTypeServerEnum.Question:
                return createConversionResultForContentItem(convertToQuestion(contentItem, parentId));
            case ContentTypeServerEnum.SequencedFlowStep:
                return createConversionResultForContentItem(convertToStep(contentItem, parentId));
            case ContentTypeServerEnum.Section:
                return convertSection(contentItem, parentId);
            default:
                throw Error(`Unexpected type ${contentItem.itemType}`);
        }
    };
    // NonSequenced Flow
    const convertHotspot = createConvertItemWithChildren({
        getChildren: (hotspotServer) => hotspotServer.hotspotTabs,
        convertChild: (serverModel, parentId) => createConversionResultForContentItem(convertToHotspotTab(serverModel, parentId)),
        convertParent: (serverModel, childrenIds, parentId) => {
            return getValidDomainModelOnly(HotspotHelper.build({
                position: convertArrowFromServerModel(serverModel.position),
                parentId,
                trackingIdentifier: serverModel.trackingIdentifier,
                tabs: childrenIds,
                modalSize: convertModalSizeFromServerModel(serverModel === null || serverModel === void 0 ? void 0 : serverModel.properties),
                id: serverModel.guid,
            }));
        },
    });
    const convertNonSequenced = (serverModel, parentId) => addConvertedStartingMessage(createConvertItemWithChildren({
        getChildren: (serverModel) => serverModel.hotspots,
        convertChild: (hotspotServer, parentId) => {
            return convertHotspot(hotspotServer, parentId);
        },
        convertParent: (serverModel, childrenIds, parentId) => {
            var _a, _b, _c;
            return getValidDomainModelOnly(NonSequencedFlowItemHelper.build({
                parentId,
                title: serverModel.name,
                startingPosition: (_a = serverModel.startingPosition) !== null && _a !== void 0 ? _a : undefined,
                trackingIdentifier: serverModel.trackingIdentifier,
                isMandatory: serverModel.isMandatory,
                startingMessageId: (_c = (_b = serverModel.startingMessage) === null || _b === void 0 ? void 0 : _b.guid) !== null && _c !== void 0 ? _c : undefined,
                hotspots: childrenIds,
                id: serverModel.guid,
            }));
        },
    })(serverModel, parentId), serverModel);
    // Resources
    const convertResource = (resourceServer, parentId) => {
        const convertedContent = ContentConverter.convertToDomainModel(resourceServer.content);
        return createConversionResultForContentItem(ResourceHelper.build({
            title: resourceServer.title,
            content: convertedContent,
            audioAsset: resourceServer.audioAsset,
            id: resourceServer.guid,
            referenceIdentifier: resourceServer.referenceIdentifier,
            parentId,
        }));
    };
    const convertResourcesFolder = createConvertItemWithChildren({
        getChildren: (serverModel) => serverModel.resources,
        convertChild: (serverModel, parentId) => convertResource(serverModel, parentId),
        convertParent: (serverModel, childIds) => {
            return getValidDomainModelOnly(ResourcesFolderHelper.build({
                resources: childIds,
                title: serverModel.name,
                id: serverModel.guid,
            }));
        },
    });
    // Quiz
    const convertQuizContentItems = (childItem, parentId) => {
        switch (childItem.itemType) {
            case ContentTypeServerEnum.BlindMapQuestion:
                return createConversionResultForContentItem(getValidDomainModelOnly(convertToBlindMapQuestion(childItem, parentId)));
            case ContentTypeServerEnum.Question:
                return createConversionResultForContentItem(convertToQuestion(childItem, parentId));
            default:
                throw Error(`Unexpected item type ${childItem.itemType}`);
        }
    };
    const convertQuiz = createConvertItemWithChildren({
        getChildren: (serverModel) => serverModel.quizItems,
        convertChild: convertQuizContentItems,
        convertParent: (serverModel, itemIds, parentId) => {
            return getValidDomainModelOnly(QuizHelper.build({
                title: serverModel.name,
                trackingIdentifier: serverModel.trackingIdentifier,
                id: serverModel.guid,
                isMandatory: serverModel.isMandatory,
                successRate: serverModel.successRate,
                startingPosition: serverModel.startingPosition,
                contentItems: itemIds,
                parentId,
            }));
        },
    });
    // Menu item
    const convertMenuItem = (serverModel, parentId) => addConvertedStartingMessage(createConvertItemWithChildren({
        getChildren: (serverModel) => serverModel.children,
        convertChild: (i, parentId) => {
            return convertItem(i, parentId);
        },
        convertParent: (serverModel, itemIds, parentId) => getValidDomainModelOnly(MenuItemHelper.build({
            childItems: itemIds,
            title: serverModel.name,
            trackingIdentifier: serverModel.trackingIdentifier,
            id: serverModel.guid,
            parentId,
        })),
    })(serverModel, parentId), serverModel);
    return convertItems;
};
